import { focusOmniBar } from '../utils/omniBarFocus';
import useEventListener from './useEventListener.hooks';

/**
 * Hand the caret back to the omnibar after a click that landed somewhere with no claim
 * on it.
 *
 * ## Why this exists
 *
 * The whole TTS fast-path is bound to the omnibar INPUT, so it fires only while that
 * input has DOM focus: Enter on an empty box (which is not "send my text" — it walks
 * the auto-prompt / slash-command / dialog-step chain, i.e. the combo-size prompt),
 * the four arrows, Shift+arrow cart navigation, Shift+Backspace.
 *
 * The omnibar used to refocus itself on EVERY render, and with transcript lines and
 * cart updates landing constantly, the caret always came back on its own — so an agent
 * could click a pill or a cart item and the shortcuts still worked. Removing that grab
 * (it made the flag panel's detail field untypable and looped against the take-over
 * dialog's focus trap) also removed the thing keeping the shortcuts alive, and agents
 * reported having to click the omnibar before every single prompt.
 *
 * So the caret is handed back on a CLICK instead. Same effect for the agent, but at a
 * deliberate moment rather than racing every re-render — which is what lets the guards
 * in `somethingElseOwnsTheCaret` actually hold.
 *
 * ## `mouseup` AND `click`, because a pill that disables itself never clicks
 *
 * Listening on `click` alone missed the intent pills entirely, which is the second
 * report this hook has had. The pills fire on `onMouseUp` (both layouts), and
 * `onIntentsClick` synchronously dispatches `setIntentClickable(idx, false)` to hold
 * the intent for `DISABLE_INTENT_INTERVAL`. Under React 17 a state update inside a
 * NATIVE handler is not batched, so that re-render flushes right there in the mouseup
 * — and the button carries `disabled` before the browser ever dispatches the `click`.
 * A disabled control receives none, so nothing on window fired and the agent was back
 * to clicking the box.
 *
 * This is the same shape as the accident documented in `useIntentHotkeys`: a pill
 * click whose `click` never lands because the DOM changed under it during mouseup.
 * `mouseup` has already fired by then, so it is the reliable signal.
 *
 * `click` is kept alongside it for the keyboard: Enter or Space on a focused control
 * fires `click` with no mouse events at all. The handback is idempotent and guarded,
 * so a real mouse press running it twice costs nothing.
 *
 * ## Bubble phase, not capture
 *
 * React 17 attaches at its root container, so by the time a window BUBBLE listener
 * runs, React's own handlers have already run and flushed. Capture would put us ahead
 * of the pill we are supposed to be leaving alone — and ahead of the very state update
 * that makes this case tricky.
 *
 * ## …and deferred a macrotask, which is load-bearing
 *
 * A click that opens the flag panel or the ASR-missing confirm mounts that surface
 * during React's flush. The panel then claims its detail field on its OWN
 * `setTimeout(0)` — queued while React flushed, i.e. BEFORE this listener even runs.
 * Focusing synchronously here would therefore win the race and steal the field: the
 * agent watches a focused panel and types into the order box.
 *
 * Deferring puts our guard check last, after the opened surface has taken what it
 * wanted, so `somethingElseOwnsTheCaret` sees the truth and stands down. Same
 * reasoning as the deferrals in `useIntentHotkeys` and `IssueFlagPanel`.
 *
 * ## The one thing checked here rather than in the shared guard
 *
 * An active text selection, because it is a property of the CLICK that `focusOmniBar`
 * cannot see. Focusing an element collapses the selection, so an agent dragging across
 * the transcript to read or copy an order note would lose it under them. A drag still
 * ends in a `click` on the common ancestor, so this is the only place to catch it.
 *
 * Nothing else is checked here. A click on `data-issue-report` — the flag button, whose
 * whole purpose is to give the caret away — needs no special case: by the time the
 * deferred handback runs, the panel it opened is mounted and carries
 * `CARET_OWNER_SELECTOR`, so the shared guard already stands down. A target-side check
 * for it was tried and removed; no test could distinguish it, which is the point.
 */
const useOmniBarFocusHandback = () => {
  const handlePress = () => {
    // Non-empty selection means a drag, not a click to get on with the order.
    const selection =
      typeof window !== 'undefined' && window.getSelection
        ? window.getSelection()
        : null;
    if (selection && !selection.isCollapsed && selection.toString().trim()) {
      return;
    }
    // Deferred, and re-guarded inside focusOmniBar — see the note above. Deferring
    // also lets the intent's own synchronous re-render (which disables the pill and
    // blurs it) finish first, so the caret is genuinely free when we check.
    setTimeout(focusOmniBar, 0);
  };

  // Both, deliberately — see the note above. Not interchangeable: `mouseup` is the one
  // that survives a control disabling itself, `click` is the one the keyboard sends.
  useEventListener('mouseup', handlePress, window);
  useEventListener('click', handlePress, window);
};

export default useOmniBarFocusHandback;
